import React, { useEffect, useState } from "react";
import langData from "../../locales/langData.json";

/**
 * ContactFormModal
 * Inline-styled modal form for safe production usage
 * Works with language prop ("en" / "ar")
 */
export default function ContactFormModal({ language, onClose }) {
  const t = langData[language].contactform;
  const v = langData[language].validation;

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  const [errors, setErrors] = useState({});

  const dir = language === "ar" ? "rtl" : "ltr";

  // 🔹 CHANGE: clear error on typing
  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setErrors((prev) => ({ ...prev, [e.target.name]: "" }));
  };

  // 🔹 VALIDATION
  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = "required";
    if (!formData.phone.trim()) newErrors.phone = "required";
    if (!formData.message.trim()) newErrors.message = "required";

    if (!formData.email.trim()) {
      newErrors.email = "required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "email";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 🔹 CHANGE: call validate()
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      await fetch(
        "https://script.google.com/macros/s/AKfycbwdSOqdD-RZZN20qA13-_UAXHOWmK_fmMjYWb9OFWvamCIlNzKRd9eo3-qtcCAQfNv-QQ/",
        {
          method: "POST",
          mode: "no-cors",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
        }
      );
      alert("✅ Message sent successfully!");
      onClose?.();
    } catch (err) {
      alert("❌ Error: " + (err.message || "Failed to send"));
    }
  };

  // Prevent background scroll while open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => (document.body.style.overflow = prev);
  }, []);

  // Allow ESC key to close
  useEffect(() => {
    const esc = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);

  // 🔹 Inline Styles (UNCHANGED)
  const overlayStyle = {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.6)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 2147483647,
    padding: "2rem",
  };

  const modalStyle = {
    background: "#fff",
    borderRadius: 12,
    width: "100%",
    maxWidth: 480,
    boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
    position: "relative",
    padding: "18px 22px",
    direction: dir,
  };

  const closeStyle = {
    position: "absolute",
    top: 10,
    [dir === "rtl" ? "left" : "right"]: 12,
    background: "transparent",
    border: "none",
    fontSize: 20,
    cursor: "pointer",
    lineHeight: 1,
  };

  const labelStyle = {
    fontWeight: 600,
    fontSize: 14,
    marginBottom: 4,
    display: "block",
  };

  const inputStyle = {
    width: "100%",
    padding: "10px 12px",
    marginBottom: 6,
    borderRadius: 8,
    border: "1px solid #ccc",
    fontSize: 14,
    boxSizing: "border-box",
  };

  const errorStyle = {
    color: "#d32f2f",
    fontSize: 12,
    marginBottom: 8,
    display: "block",
    textAlign: dir === "rtl" ? "right" : "left",
  };

  const submitStyle = {
    width: "100%",
    background: "#683FEA",
    color: "#fff",
    padding: "10px 12px",
    borderRadius: 8,
    border: "none",
    fontSize: 16,
    fontWeight: 600,
    cursor: "pointer",
    marginTop: 4,
  };

  return (
    <div style={overlayStyle} onClick={onClose} dir={dir}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} aria-label="Close" style={closeStyle}>
          ✕
        </button>

        <h3
          style={{
            marginTop: 2,
            marginBottom: 16,
            textAlign: dir === "rtl" ? "right" : "left",
          }}
        >
          {t.formTitle || (language === "ar" ? "نموذج الاتصال" : "Partners")}
        </h3>

        <form onSubmit={handleSubmit} noValidate>
          <label style={labelStyle}>{t.fullName}</label>
          <input
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder={t.placeholderName}
            style={inputStyle}
          />
          {errors.name && <span style={errorStyle}>{v[errors.name]}</span>}

          <label style={labelStyle}>{t.email}</label>
          <input
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder={t.placeholderEmail}
            style={inputStyle}
          />
          {errors.email && <span style={errorStyle}>{v[errors.email]}</span>}

          <label style={labelStyle}>{t.phone}</label>
          <input
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder={t.placeholderPhone}
            style={inputStyle}
          />
          {errors.phone && <span style={errorStyle}>{v[errors.phone]}</span>}

          <label style={labelStyle}>{t.message}</label>
          <textarea
            name="message"
            rows={4}
            value={formData.message}
            onChange={handleChange}
            placeholder={t.placeholderMessage}
            style={{ ...inputStyle, resize: "vertical" }}
          />
          {errors.message && (
            <span style={errorStyle}>{v[errors.message]}</span>
          )}

          <button type="submit" style={submitStyle}>
            {t.submit}
          </button>
        </form>
      </div>
    </div>
  );
}
